import { Typography } from '@material-ui/core';
import { useFormikEnhanced } from '@superdispatch/forms';
import { ColorDynamic, Stack, useSnackbarStack } from '@superdispatch/ui';
import { Alert, Box, Button } from '@superdispatch/ui-lab';
import { useUsersAPI } from '../../users/data/UsersAPI';
import { UserDTO } from '../../users/data/UsersDTO';
import { useState } from 'react';
import { APIError, createAPIError, isAPIError } from 'shared/api/APIError';
import { FormikDrawerContent } from 'shared/form/FormikDrawer';
import {
  DriverDTO,
  driverEditSchema,
  inviteDriverDTO,
} from 'shared/modules/driver/DriverDTO';
import { LinkAnchor } from 'shared/routing/Links';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import styled from 'styled-components';
import { JOIN_REQUEST_ALREADY_SENT_ERROR } from '../../drivers/data/DriverPageUtils';
import { trackDriversEvent } from '../../drivers/data/DriversAnalytics';
import { useAcceptDriverRequest } from '../../drivers/data/DriversAPI';
import { LoadDTO } from '../data/LoadDTO';
import { useLoadsContext } from '../data/LoadsContext';
import { DriverFormFields } from 'shared/modules/driver/DriverFormFields';
import { AddDriverPlanUpdateDialog } from 'shared/modules/driver/AddDriverPlanUpdateDialog';

const Link = styled(LinkAnchor)`
  text-decoration: underline !important;
`;

const StyledBox = styled(Box)`
  padding-top: 6px;
  padding-bottom: 6px;
`;

const StyledTypography = styled(Typography)`
  font-size: 13px;
  color: ${ColorDynamic.Dark500};
`;

interface LoadsInviteDriverFormProps {
  onClose: () => void;
  onSuccess: (driver: DriverDTO | UserDTO | null) => void;
  load?: LoadDTO;
}

export function LoadsInviteDriverForm({
  onClose,
  load,
  onSuccess,
}: LoadsInviteDriverFormProps) {
  const loadGUID = load?.guid || '';
  const { addSnackbar } = useSnackbarStack();
  const [requestedDriverGUID, setRequestedDriverGUID] = useState<
    string | undefined
  >();
  const [shouldShowDriversLinkAlert, setShowDriversLinkAlert] = useState(true);
  const [shouldConfirm, setShouldConfirm] = useState(false);
  const { inviteUser } = useUsersAPI();
  const { data: settings } = useCarrierSettings();
  const { loadsAPI } = useLoadsContext();
  const { mutateAsync: acceptDriverRequest, isPending } =
    useAcceptDriverRequest({
      onSuccess: async () => {
        await loadsAPI.assignDriver(loadGUID, requestedDriverGUID || '');
        onClose();
        handleClose();
      },
      onError: (e) => {
        addSnackbar(e.message, { variant: 'error' });
      },
    });

  const formik = useFormikEnhanced({
    validationSchema: inviteDriverDTO,
    initialValues: { email: '', phone_number: '', name: '' },
    async onSubmit(values) {
      const user = await inviteUser({ ...values, roles: ['driver'] });
      const currentDriver = user.data.driver;
      await loadsAPI.assignDriver(loadGUID, currentDriver?.guid || '');

      return user.data;
    },
    onSubmitSuccess(response: DriverDTO | UserDTO) {
      onSuccess(response);
      const guid: string = getGuid(response);

      trackDriversEvent({
        name: 'CTMS: Driver Invited',
        properties: {
          guid,
          source: 'Assign Loads Page',
          userGuid: settings?.user.guid,
        },
      });

      onClose();
      handleClose();
    },
    onSubmitFailure: (e) => {
      if (isAPIError(e) && e.type === JOIN_REQUEST_ALREADY_SENT_ERROR) {
        const error = createAPIError(e) as APIError<{ guid: string }>;
        setRequestedDriverGUID(error.context?.guid);
      }
    },
  });

  const { isSubmitting, values } = formik;

  function handleClose() {
    setRequestedDriverGUID(undefined);
  }

  return (
    <>
      <AddDriverPlanUpdateDialog
        open={shouldConfirm}
        onClose={() => {
          setShouldConfirm(false);
        }}
        onClick={() => {
          setShouldConfirm(false);
          void formik.submitForm();
        }}
      />

      <FormikDrawerContent
        onClose={() => {
          onClose();
          handleClose();
        }}
        formik={formik}
      >
        <Box maxWidth="368px">
          <Stack space="small">
            {shouldShowDriversLinkAlert && (
              <Alert
                onClose={() => {
                  setShowDriversLinkAlert(false);
                }}
                severity="info"
              >
                You can manage drivers from{' '}
                <Link underline="none" color="primary" to="/users">
                  Users page
                </Link>{' '}
                on navigation bar.
              </Alert>
            )}

            <DriverFormFields type="invite" hasWarning={false} />

            {requestedDriverGUID && (
              <StyledBox
                paddingLeft="xsmall"
                paddingRight="xsmall"
                backgroundColor="Blue50"
                borderRadius="small"
              >
                <StyledTypography>
                  This driver has already requested to join your carrier. Would
                  you like to accept the driver&apos;s request and assign the
                  load?
                </StyledTypography>
              </StyledBox>
            )}

            <Box paddingTop="small">
              {requestedDriverGUID ? (
                <Button
                  disabled={isPending}
                  onClick={() => {
                    void acceptDriverRequest([
                      requestedDriverGUID,
                      values.name,
                    ]);
                  }}
                >
                  Accept & Assign
                </Button>
              ) : (
                <Button
                  disabled={isSubmitting}
                  onClick={() => {
                    if (
                      settings?.is_paying &&
                      driverEditSchema.isValidSync(formik.values)
                    ) {
                      setShouldConfirm(true);
                    } else {
                      void formik.submitForm();
                    }
                  }}
                >
                  Invite & Assign
                </Button>
              )}
            </Box>
          </Stack>
        </Box>
      </FormikDrawerContent>
    </>
  );
}

function getGuid(response: DriverDTO | UserDTO): string {
  return 'driver' in response ? response.driver?.guid || '' : response.guid;
}
